import { C, MATERIALS, PARTICLES, MCS_CONSTANT } from '../data/physicsConstants';
import { calculateKinematics, calculateMCSAngle, calculateMCSVariance } from '../utils/physicsUtils';

// ============================================
// Z-SCANNER: PROTON MCS TOMOGRAPHY PHYSICS ENGINE
// ============================================
// Multiple Coulomb Scattering simulation for material discrimination
// Based on Highland formula for θ_RMS calculation

// ============================================
// SEEDED RANDOM NUMBER GENERATOR
// ============================================
// Mulberry32 PRNG for reproducible simulations
function mulberry32(seed) {
    return function () {
        let t = seed += 0x6D2B79F5;
        t = Math.imul(t ^ t >>> 15, t | 1);
        t ^= t + Math.imul(t ^ t >>> 7, t | 61);
        return ((t ^ t >>> 14) >>> 0) / 4294967296;
    }
}

// Box-Muller transform for Gaussian random numbers
function gaussianRandom(mean = 0, sigma = 1, rng) {
    const u1 = rng();
    const u2 = rng();
    const z0 = Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2);
    return mean + z0 * sigma;
}

export class PhysicsEngine {
    constructor(params) {
        this.params = params;
        this.seed = params.seed || Date.now();
        this.rng = mulberry32(this.seed);

        this.detectorResponse = {
            enabled: params.detectorResponse?.enabled ?? true,
            positionResolution: params.detectorResponse?.positionResolution || 0.05,
            efficiency: params.detectorResponse?.efficiency || 0.92,
            angularResolution: params.detectorResponse?.angularResolution || 0.1
        };
    }

    // Simplified Energy Loss (Bethe-Bloch-like)
    calculateEnergyLoss(momentum, mass, materialName, thickness) {
        // dE/dx ~ rho * Z/A * 1/beta^2
        // Simplified model for visualization
        const mat = MATERIALS[materialName] || MATERIALS.water;
        const kinematics = calculateKinematics(momentum, mass);
        const beta = kinematics.beta;
        
        // Approximate dE/dx in MeV/cm
        // Minimum Ionizing Particle (MIP) is approx 2 MeV/(g/cm^2)
        // At low beta, 1/beta^2 rises.
        let dEdx = 2 * mat.density * (1 / (beta * beta));
        
        // Bragg peak enhancement at very low energy
        if (beta < 0.1) dEdx *= 2; 

        const energyLossMeV = dEdx * thickness;
        return energyLossMeV / 1000; // GeV
    }

    // calculateTransitionRadiation is not in utils yet, but I saw it in the file content I'm replacing.
    // Wait, I didn't add calculateTransitionRadiation to utils.
    // I should keep it here or add it to utils.
    // The previous implementation had it.
    // "calculateTransitionRadiation(gamma, material1Name)"
    // It's simple enough to keep or move.
    // I'll keep it as a class method or helper if it's specific. 
    // Actually, looking at my imports, I imported `calculateTransitionRadiation`?
    // No, I didn't put it in `physicsUtils.js`.
    // So I should keep it here.
    
    calculateTransitionRadiation(gamma, material1Name) {
         // TR probability scales with alpha * gamma
        // Only significant for gamma > 1000 (electrons)
        if (gamma < 1000) return 0;
        return 0.01 * (gamma / 1000); // Probability per interface
    }

    async runAsync(onProgress) {
        const {
            momentum = 0.7,
            mass = PARTICLES.proton.mass,
            particleLifetime = Infinity, // Add lifetime parameter
            material = 'pvc',
            materialX0 = null,
            phantomThickness = 3,
            beamIntensity = 10000,
            insertMaterial = null,
            insertMaterialX0: insertX0 = null,
            insertRadius = 0.5,
            phantomSize = 3,
            physics = {},
            beamlineLength = 10 // Need length for TOF
        } = this.params;

        const N = beamIntensity;
        const mat = MATERIALS[material] || MATERIALS.pvc;
        const insertMat = insertMaterial ? (MATERIALS[insertMaterial] || mat) : mat;
        
        const kinematics = calculateKinematics(momentum, mass);

        const results = {
            events: [],
            stats: {
                total: N,
                detected: 0,
                materialEvents: 0,
                insertEvents: 0,
                meanScatteringAngle: 0,
                rmsScatteringAngle: 0,
                stoppedParticles: 0,
                transitionRadiationPhotons: 0,
                secondaryParticles: 0,
                meanLifetime: 0, // New stat
                peakDepth: 0 // New stat
            },
            histogram: {
                bins: [],
                materialBins: [],
                insertBins: []
            },
            // New Specialized Result Arrays
            depthDose: new Array(100).fill(0), // Medical: Dose vs Depth
            decayTimes: new Array(50).fill(0), // Decay: Count vs Time
            tofSpectrum: new Array(100).fill(0), // PID: Count vs Time
            
            params: {
                momentum,
                material: mat.name,
                thickness: phantomThickness,
                beta: kinematics.beta,
                gamma: kinematics.gamma
            }
        };

        const nBins = 50;
        const binWidth = 1;
        for (let i = 0; i < nBins; i++) {
            results.histogram.bins.push(0);
            results.histogram.materialBins.push(0);
            results.histogram.insertBins.push(0);
        }

        let sumTheta = 0;
        let sumThetaSq = 0;
        let sumLifetime = 0;
        let sumDepth = 0;

        const chunkSize = 500; // Process 500 events per chunk
        
        for (let i = 0; i < N; i++) {
             // Non-blocking yield every chunk
            if (i % chunkSize === 0) {
                if (onProgress) onProgress(Math.round((i / N) * 100));
                await new Promise(resolve => setTimeout(resolve, 0));
            }

            const x = (this.rng() - 0.5) * phantomSize;
            const y = (this.rng() - 0.5) * phantomSize;
            const r = Math.sqrt(x * x + y * y);
            const throughInsert = insertMaterial && r < insertRadius;
            
            const currentMat = throughInsert ? insertMat : mat;
            const currentX0 = throughInsert ? (insertX0 || currentMat.X0) : (materialX0 || currentMat.X0);

            let detected = true;
            let finalMomentum = momentum;
            let stopped = false;
            let stopDepth = 0;
            let decayTime = Infinity;

            // 1. Energy Loss (Medical Physics)
            if (physics.energyLossEnabled) {
                // Determine range/stopping power
                // Simple Bragg Peak simulation: Range ~ E^1.75
                // We simulate stopping depth variation
                const eLoss = this.calculateEnergyLoss(momentum, mass, currentMat.name.toLowerCase().split(' ')[0], phantomThickness);
                const initialE = Math.sqrt(momentum**2 + mass**2);
                
                if (initialE - mass - eLoss <= 0) {
                    stopped = true;
                    results.stats.stoppedParticles++;
                    
                    // Randomize stopping depth around mean range
                    // Mean range R approx proportional to p^3.5 / mass^1.5 (Bethe-Bloch approx)
                    // For visualization: Map momentum to phantom thickness
                    const meanRange = (momentum / 0.2) * (phantomThickness / 2); // Calibrate to 0.2 GeV -> half phantom
                    const straggle = meanRange * 0.05; // 5% range straggling
                    stopDepth = meanRange + gaussianRandom(0, straggle, this.rng);
                    
                    if (stopDepth > 0 && stopDepth < phantomThickness * 2) {
                        const bin = Math.min(99, Math.floor((stopDepth / (phantomThickness * 1.5)) * 100));
                        results.depthDose[bin]++;
                        sumDepth += stopDepth;
                    }
                } else {
                     const newE = initialE - eLoss;
                     finalMomentum = Math.sqrt(newE**2 - mass**2);
                }
            }

            // 2. Particle Decay (Lifetime Physics)
            if (physics.decayEnabled && particleLifetime < Infinity) {
                // Sample exponential decay time (proper time)
                const properTime = -Math.log(this.rng()) * particleLifetime;
                // Lab time = gamma * proper time
                decayTime = properTime * kinematics.gamma;
                
                // Flight distance before decay
                const decayDist = decayTime * kinematics.beta * C / 1e9; // C is m/s -> m/ns? No, C is ~3e8. 
                // Let's assume standard units: C=3e8 m/s. Lifetime in seconds.
                // Distance = v * t = (beta * c) * t
                const dist = kinematics.beta * C * decayTime; // meters

                // Check if decays before detector
                if (dist < beamlineLength) {
                    detected = false; // Decayed before end
                    
                    // Bin decay times for histogram
                    const timeBin = Math.min(49, Math.floor((properTime / (particleLifetime * 5)) * 50));
                    results.decayTimes[timeBin]++;
                    sumLifetime += properTime;
                }
            }

            // 3. Time of Flight (PID)
            if (detected) {
                // TOF = distance / velocity
                const velocity = kinematics.beta * C; 
                const tof = beamlineLength / velocity; // seconds
                // Add timing resolution (e.g., 100 ps)
                const measuredTof = tof + gaussianRandom(0, 1e-10, this.rng);
                
                // Bin TOF (centered around expected TOF)
                // We'll normalize this later or use relative bins
                const bin = Math.floor((measuredTof * 1e9) % 100); // ns bins? Just dummy binning for now
                // Actually, let's just push raw values for better charting if needed, or simple binning
                // For simulator: Bin relative to expected
                const expectedTof = beamlineLength / (kinematics.beta * C);
                const deltaT = (measuredTof - expectedTof) * 1e9; // ns difference
                const tofBin = Math.min(99, Math.max(0, Math.floor(50 + deltaT * 10))); // +/- 5ns range
                results.tofSpectrum[tofBin]++;
            }

            // Transition Radiation
            if (physics.transitionRadiationEnabled && !stopped) {
                 if (this.rng() < this.calculateTransitionRadiation(kinematics.gamma, currentMat.name)) {
                     results.stats.transitionRadiationPhotons++;
                 }
            }
            
            // Production Modes
            if (physics.productionMode && !stopped) {
                const r = this.rng();
                if (physics.productionMode === 'delta' && r < 0.05) results.stats.secondaryParticles++;
                if (physics.productionMode === 'pair' && r < 0.1) results.stats.secondaryParticles += 2;
                if (physics.productionMode === 'shower' && r < 0.2) results.stats.secondaryParticles += 10;
                if (physics.productionMode === 'synchrotron' && r < 0.8) results.stats.secondaryParticles++; // High photon yield
                if (physics.productionMode === 'moller' && r < 0.1) results.stats.secondaryParticles++;
                if (physics.productionMode === 'bhabha' && r < 0.1) results.stats.secondaryParticles++;
                if (physics.productionMode === 'bremsstrahlung' && r < 0.15) results.stats.secondaryParticles++; // Photon
                if (physics.productionMode === 'absorption' && r < 0.05) {
                    stopped = true;
                    results.stats.stoppedParticles++;
                }
            }

            // MCS (using average momentum if energy loss)
            const avgMomentum = (momentum + (stopped ? 0.001 : finalMomentum)) / 2;
            const thetaRMS = calculateMCSAngle(avgMomentum, mass, phantomThickness, currentX0);

            const thetaX = gaussianRandom(0, thetaRMS, this.rng);
            const thetaY = gaussianRandom(0, thetaRMS, this.rng);
            const theta = Math.sqrt(thetaX ** 2 + thetaY ** 2);

            // Detection logic update
            if (stopped) detected = false;

            if (this.detectorResponse.enabled && detected) {
                if (this.rng() > this.detectorResponse.efficiency) detected = false;
            }
            
            let measuredTheta = theta;
            if (detected) {
                const angularNoise = this.detectorResponse.angularResolution * 1e-3;
                measuredTheta = Math.sqrt(
                    (thetaX + gaussianRandom(0, angularNoise, this.rng)) ** 2 +
                    (thetaY + gaussianRandom(0, angularNoise, this.rng)) ** 2
                );

                results.stats.detected++;
                if (throughInsert) results.stats.insertEvents++; else results.stats.materialEvents++;
                sumTheta += measuredTheta;
                sumThetaSq += measuredTheta ** 2;

                const thetaMrad = measuredTheta * 1000;
                const binIndex = Math.min(Math.floor(thetaMrad / binWidth), nBins - 1);
                results.histogram.bins[binIndex]++;
                if (throughInsert) results.histogram.insertBins[binIndex]++; else results.histogram.materialBins[binIndex]++;
            }

            if (i < 500) {
                results.events.push({
                    id: i, x, y, 
                    momentum: finalMomentum, 
                    theta: measuredTheta * 1000,
                    detected, throughInsert,
                    material: currentMat.name
                });
            }
        }

        if (results.stats.detected > 0) {
            const theta2D_RMS = Math.sqrt(sumThetaSq / results.stats.detected) * 1000;
            results.stats.meanScatteringAngle = (sumTheta / results.stats.detected) * 1000;
            results.stats.rmsScatteringAngle = theta2D_RMS / Math.sqrt(2);
        }
        
        if (results.stats.stoppedParticles > 0) {
            results.stats.peakDepth = sumDepth / results.stats.stoppedParticles;
        }

        results.seed = this.seed;
        return results;
    }

    calculateVarianceContrast(material1, material2, momentum, thickness) {
        // This function doesn't need mass? calculateMCSVariance needs mass!
        // The original code was: 
        // const theta1 = this.calculateMCSAngle(momentum, thickness, MATERIALS[material1].X0);
        // It missed mass!
        // We should default to PROTON or require mass.
        // I will default to PROTON_MASS here as it's a static utility-like check usually for Z-Scanning.
        const mass = PARTICLES.proton.mass; 
        const theta1 = calculateMCSAngle(momentum, mass, thickness, MATERIALS[material1].X0);
        const theta2 = calculateMCSAngle(momentum, mass, thickness, MATERIALS[material2].X0);
        const variance1 = theta1 ** 2;
        const variance2 = theta2 ** 2;
        const contrast = Math.abs(variance1 - variance2) / Math.max(variance1, variance2);
        return {
            theta1: theta1 * 1000, theta2: theta2 * 1000,
            variance1: variance1 * 1e6, variance2: variance2 * 1e6,
            contrast: contrast * 100, discriminable: contrast > 0.05
        };
    }

    reconstructImage(events, gridSize = 30) {
        const grid = [];
        const counts = [];
        const phantomSize = this.params.phantomSize || 3;
        const cellSize = phantomSize / gridSize;

        for (let i = 0; i < gridSize; i++) {
            grid[i] = []; counts[i] = [];
            for (let j = 0; j < gridSize; j++) { grid[i][j] = 0; counts[i][j] = 0; }
        }

        for (const event of events) {
            if (!event.detected) continue;
            const xi = Math.floor((event.x + phantomSize / 2) / cellSize);
            const yi = Math.floor((event.y + phantomSize / 2) / cellSize);
            if (xi >= 0 && xi < gridSize && yi >= 0 && yi < gridSize) {
                grid[xi][yi] += event.theta ** 2;
                counts[xi][yi]++;
            }
        }

        for (let i = 0; i < gridSize; i++) {
            for (let j = 0; j < gridSize; j++) {
                if (counts[i][j] > 0) grid[i][j] = grid[i][j] / counts[i][j];
            }
        }
        return { grid, counts, gridSize, cellSize };
    }
}